import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'failed'>('checking');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const addDebugInfo = (message: string) => {
    console.log(message);
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Test Supabase connection on load
  useEffect(() => {
    const testConnection = async () => {
      try {
        addDebugInfo('Testing Supabase connection...');
        
        const { data, error } = await supabase
          .from('profiles')
          .select('count', { count: 'exact', head: true });
        
        if (error) {
          addDebugInfo(`Connection test failed: ${error.message}`);
          setConnectionStatus('failed');
          setError('Cannot connect to authentication server. Please check your internet connection.');
        } else {
          addDebugInfo('✅ Supabase connection successful');
          setConnectionStatus('connected');
        }
      } catch (err: any) {
        addDebugInfo(`Connection error: ${err.message}`);
        setConnectionStatus('failed');
        setError('Cannot connect to authentication server. Please refresh the page.');
      }
    };

    testConnection();
  }, []);

  // Check for messages from URL params
  useEffect(() => {
    const message = searchParams.get('message');
    if (message) {
      setSuccess(message);
    }
  }, [searchParams]);

  // Load saved email if "Remember Me" was checked previously
  useEffect(() => {
    const savedEmail = localStorage.getItem('pepcoin_remembered_email');
    if (savedEmail) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail,
        rememberMe: true
      }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setDebugInfo([]);
    setLoading(true);

    try {
      const email = formData.email.trim().toLowerCase();
      const password = formData.password;

      // Basic validation
      if (!email || !password) {
        throw new Error('Please enter both email and password');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      addDebugInfo('🔐 Starting sign-in process...');
      addDebugInfo(`Email: ${email}`);
      addDebugInfo('Attempting authentication...');

      // Attempt sign in
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      addDebugInfo(`Response received - User: ${!!data?.user}, Session: ${!!data?.session}, Error: ${!!signInError}`);

      if (signInError) {
        addDebugInfo(`❌ Sign-in error: ${signInError.message}`);
        
        // Handle specific error cases
        if (signInError.message.includes('Invalid login credentials')) {
          throw new Error('Incorrect email or password. Please check your credentials and try again.');
        }
        
        if (signInError.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email first. Check your inbox for the verification link.');
        }

        if (signInError.message.includes('Email link is invalid')) {
          throw new Error('Your session has expired. Please try signing in again.');
        }

        if (signInError.message.includes('User not found')) {
          throw new Error('No account found with this email. Please sign up first.');
        }
        
        throw new Error(signInError.message);
      }

      if (!data?.user) {
        addDebugInfo('❌ No user data returned');
        throw new Error('Sign-in failed - no user data received. Please try again.');
      }

      if (!data?.session) {
        addDebugInfo('❌ No session created');
        throw new Error('Sign-in failed - session not created. Please try again.');
      }

      addDebugInfo('✅ Sign-in successful!');
      addDebugInfo(`User ID: ${data.user.id}`);
      addDebugInfo(`Email verified: ${data.user.email_confirmed_at ? 'Yes' : 'No'}`);
      addDebugInfo('✅ Session created and stored');
      
      // Save email if remember me is checked
      if (formData.rememberMe) {
        localStorage.setItem('pepcoin_remembered_email', email);
        addDebugInfo('✅ Email saved for next time');
      } else {
        localStorage.removeItem('pepcoin_remembered_email');
      }

      setSuccess('Sign-in successful! Redirecting to your wallet...');
      addDebugInfo('🚀 Redirecting to wallet page...');
      
      // Wait longer to ensure session is fully established
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Verify session one more time before redirect
      const { data: { session: verifySession } } = await supabase.auth.getSession();
      
      if (verifySession) {
        addDebugInfo('✅ Session verified, redirecting now...');
        // Use window.location for a full page reload to ensure session is loaded
        window.location.href = '/wallet';
      } else {
        addDebugInfo('⚠️ Session verification failed, retrying...');
        setTimeout(() => {
          window.location.href = '/wallet';
        }, 1000);
      }
      
    } catch (err: any) {
      addDebugInfo(`❌ Error: ${err.message}`);
      setError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'rememberMe' && !checked) {
      localStorage.removeItem('pepcoin_remembered_email');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleForgotPassword = async () => {
    const email = formData.email.trim().toLowerCase();
    
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (resetError) {
        console.error('Password reset error:', resetError);
      }
      
      setSuccess('If an account exists with this email, you will receive reset instructions.');
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      setSuccess('If an account exists with this email, you will receive reset instructions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 cursor-pointer">
            <div className="relative w-12 h-12">
              <img 
                src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                alt="PepCoin Logo" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
              <div 
                className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl"
                style={{ display: 'none' }}
              >
                P
              </div>
            </div>
            <span className="text-white font-bold text-2xl bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" style={{ fontFamily: '"Orbitron", monospace' }}>
              PepCoin
            </span>
          </Link>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-gray-400">Sign in to your PepCoin account</p>
          </div>

          {/* Connection Status */}
          {connectionStatus === 'checking' && (
            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
                <p className="text-blue-400 text-sm">Connecting to server...</p>
              </div>
            </div>
          )}

          {connectionStatus === 'failed' && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-wifi-off-line text-red-400"></i>
                <p className="text-red-400 text-sm">Connection failed. Please refresh the page.</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-400"></i>
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-400 mt-0.5"></i>
                <div className="flex-1">
                  <p className="text-red-400 text-sm">{error}</p>
                  {error.includes('verify your email') && (
                    <Link 
                      to="/auth/signup" 
                      className="mt-2 text-xs text-red-300 hover:text-red-200 underline cursor-pointer"
                    >
                      Resend verification email
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || connectionStatus === 'failed'}
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="w-full px-4 py-3 pl-10 bg-gray-800/50 border border-purple-500/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                />
                <i className="ri-mail-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading || connectionStatus === 'failed'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pl-10 pr-10 bg-gray-800/50 border border-purple-500/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                />
                <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={loading}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded bg-gray-800 disabled:opacity-50 cursor-pointer"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-300 cursor-pointer">
                  Remember me
                </label>
              </div>

              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="text-sm text-purple-400 hover:text-purple-300 cursor-pointer transition-colors disabled:opacity-50"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || connectionStatus !== 'connected'}
              className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Signing In...
                </>
              ) : connectionStatus === 'failed' ? (
                <>
                  <i className="ri-wifi-off-line"></i>
                  Connection Failed
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line"></i>
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link to="/auth/signup" className="text-purple-400 hover:text-purple-300 font-medium cursor-pointer transition-colors">
                Sign up
              </Link>
            </p>
          </div>

          {/* Debug Info - Only show if there are debug messages */}
          {debugInfo.length > 0 && (
            <div className="mt-6 p-4 bg-gray-900/50 border border-gray-700 rounded-xl max-h-48 overflow-y-auto">
              <h4 className="text-gray-400 text-xs font-semibold mb-2 flex items-center gap-2">
                <i className="ri-bug-line"></i>
                Debug Log (Press F12 for more details)
              </h4>
              <div className="space-y-1">
                {debugInfo.map((info, index) => (
                  <p key={index} className="text-gray-500 text-xs font-mono">{info}</p>
                ))}
              </div>
            </div>
          )}

          {/* Troubleshooting */}
          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
              <i className="ri-question-line"></i>
              Can't Sign In?
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2 text-blue-300">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>Make sure you've verified your email address</span>
              </div>
              <div className="flex items-start gap-2 text-blue-300">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>Check that your password is correct (minimum 6 characters)</span>
              </div>
              <div className="flex items-start gap-2 text-blue-300">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>Try the "Forgot password?" option to reset</span>
              </div>
              <div className="flex items-start gap-2 text-blue-300">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>Check your internet connection</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Help */}
        <div className="text-center">
          <div className="bg-gray-800/30 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-2">Still Having Issues?</h4>
            <p className="text-gray-400 text-sm mb-3">
              Our support team is ready to help you get back into your account.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link 
                to="/help" 
                className="inline-flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 cursor-pointer text-sm font-medium bg-purple-500/10 px-4 py-2 rounded-lg hover:bg-purple-500/20 transition-all"
              >
                <i className="ri-customer-service-2-line"></i>
                Contact Support
              </Link>
              <button
                onClick={handleRefresh}
                className="inline-flex items-center justify-center gap-2 text-gray-400 hover:text-gray-300 cursor-pointer text-sm font-medium bg-gray-700/30 px-4 py-2 rounded-lg hover:bg-gray-700/50 transition-all whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
